import { useState } from 'react';
import { costCentersApi } from '../../api/services';
import type { CostCenterDto, CostCenterReportRowDto } from '../../api/types';
import { Alert, Badge, Button, Field, Form, Input, Loadable, Modal, PageHeader, Select, Table, Tabs, firstOfMonthIso, fmtMoney, todayIso, useAction, useAsync } from '../../ui/components';
import { PageHelp } from '../../ui/help';
import { PERM, useCan } from '../../ui/access';

type TabKey = 'list' | 'report';

/** Centros de costo y estado de resultados por centro. */
export function CostCentersPage() {
  const [tab, setTab] = useState<TabKey>('list');
  const centers = useAsync(() => costCentersApi.list(), []);
  return (
    <>
      <PageHeader title="Centros de costo" subtitle="Sucursales, departamentos o proyectos para separar ingresos y gastos" />
      <PageHelp page="costCenters" />
      <Tabs<TabKey> tabs={[{ key: 'list', label: 'Centros' }, { key: 'report', label: 'Resultados por centro' }]} active={tab} onChange={setTab} />
      {tab === 'list' && <CentersList state={centers} />}
      {tab === 'report' && <CenterReport />}
    </>
  );
}

function CentersList({ state }: { state: { data?: CostCenterDto[]; loading: boolean; error: unknown; reload: () => void } }) {
  const canEdit = useCan(PERM.setup);
  const [editing, setEditing] = useState<CostCenterDto | 'new' | null>(null);
  return (
    <>
      {canEdit && <div className="acc-actions" style={{ justifyContent: 'flex-start' }}><Button kind="primary" onClick={() => setEditing('new')}>Nuevo centro</Button></div>}
      <Loadable state={state}>
        {(rows) => (
          <Table<CostCenterDto> rows={rows} rowKey={(c) => c.id} empty="No hay centros de costo." columns={[
            { key: 'c', title: 'Código', render: (c) => c.code },
            { key: 'n', title: 'Nombre', render: (c) => c.name },
            { key: 's', title: 'Estado', render: (c) => (c.status === 'A' ? <Badge tone="ok">Activo</Badge> : <Badge>Inactivo</Badge>) },
            { key: 'x', title: '', render: (c) => (canEdit ? <Button kind="link" onClick={() => setEditing(c)}>Editar…</Button> : null) },
          ]} />
        )}
      </Loadable>
      {editing && <CenterModal center={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); state.reload(); }} />}
    </>
  );
}

function CenterModal({ center, onClose, onSaved }: { center: CostCenterDto | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ code: center?.code ?? '', name: center?.name ?? '', status: center?.status ?? ('A' as 'A' | 'I') });
  const { run, busy, fieldErrors } = useAction();
  return (
    <Modal title={center ? `Editar ${center.code}` : 'Nuevo centro de costo'} onClose={onClose} size="sm">
      <Form onSubmit={() => run(async () => { if (center) await costCentersApi.update(center.id, f); else await costCentersApi.create(f); onSaved(); }, { success: 'Centro guardado.' })}>
        <Field label="Código" error={fieldErrors.Code}><Input value={f.code} onChange={(v) => setF({ ...f, code: v.toUpperCase() })} maxLength={20} required /></Field>
        <Field label="Nombre" error={fieldErrors.Name}><Input value={f.name} onChange={(v) => setF({ ...f, name: v })} maxLength={100} required /></Field>
        <Field label="Estado"><Select value={f.status} onChange={(v) => setF({ ...f, status: v })} options={[{ value: 'A', label: 'Activo' }, { value: 'I', label: 'Inactivo' }]} /></Field>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Guardar</Button></div>
      </Form>
    </Modal>
  );
}

function CenterReport() {
  const [draft, setDraft] = useState({ from: firstOfMonthIso(), to: todayIso() });
  const [range, setRange] = useState(draft);
  const report = useAsync(() => costCentersApi.report(range.from, range.to), [range.from, range.to]);
  const { run, busy } = useAction();
  return (
    <>
      <div className="acc-toolbar">
        <Field label="Desde"><Input type="date" value={draft.from} onChange={(v) => setDraft({ ...draft, from: v })} /></Field>
        <Field label="Hasta"><Input type="date" value={draft.to} onChange={(v) => setDraft({ ...draft, to: v })} /></Field>
        <Button kind="primary" onClick={() => setRange(draft)}>Consultar</Button>
        <Button busy={busy} onClick={() => run(() => costCentersApi.exportReport(range.from, range.to))}>Exportar CSV</Button>
      </div>
      <Loadable state={report}>
        {(r) => {
          const keys = Array.from(new Set(r.rows.flatMap((x) => Object.keys(x.byCenter)))).sort((a, b) => Number(a) - Number(b));
          const name = (k: string) => (k === '0' ? 'Sin centro' : r.centers.find((c) => String(c.id) === k)?.code ?? k);
          return (
            <>
              <Table<CostCenterReportRowDto> rows={r.rows} rowKey={(x) => x.accountNumber} empty="Sin ingresos ni gastos en el período." columns={[
                { key: 'a', title: 'Cuenta', render: (x) => `${x.accountNumber} · ${x.accountName}` },
                ...keys.map((k) => ({ key: `c${k}`, title: name(k), align: 'right' as const, render: (x: CostCenterReportRowDto) => fmtMoney(x.byCenter[k] ?? 0) })),
                { key: 't', title: 'Total', align: 'right', render: (x) => <strong>{fmtMoney(x.total)}</strong> },
              ]} footer={
                <tr><td><strong>Resultado</strong></td>{keys.map((k) => <td key={k} style={{ textAlign: 'right' }}><strong>{fmtMoney(r.result[k] ?? 0)}</strong></td>)}<td /></tr>
              } />
              {r.notes.map((n) => <Alert key={n}>{n}</Alert>)}
            </>
          );
        }}
      </Loadable>
    </>
  );
}
